import React, { useState, useEffect } from 'react';
import { useRpiEdgeUnlock } from '../hooks/useRpiEdgeUnlock';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, ClipboardList, BarChart3, Package, Users, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, FileText, Cog, Settings, BarChart2, UserCog, AlertTriangle, ShieldAlert, Layers, KeyRound, Clock, Wrench, ClipboardCheck, Gauge, Calendar, CalendarCheck, X, ShieldCheck, Activity, Cpu, Radio, Microscope, FileCheck2, BadgeCheck, HardDrive, ClipboardPen } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { moduleRoutes } from '../lib/moduleAccess';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Sidebar({ collapsed, setCollapsed, mobileOpen = false, setMobileOpen = () => {} }) {
  const location = useLocation();
  const { user } = useAuth();
  const isMaster = user?.role === 'master';
  const isAdmin = user?.role === 'admin';
  const [mastersOpen, setMastersOpen] = useState(true);
  const [logsOpen, setLogsOpen] = useState(true);
  const [qualityOpen, setQualityOpen] = useState(true);
  const [autoJobcardEnabled, setAutoJobcardEnabled] = useState(false);
  const rpiEdgeUnlocked = useRpiEdgeUnlock();

  useEffect(() => {
    if (!user || isMaster) return;
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const { data } = await axios.get(`${API}/api/auto-jobcard/status`, { withCredentials: true });
        if (!cancelled) setAutoJobcardEnabled(!!data.enabled);
      } catch { /* silent — falls back to hidden */ }
    };
    fetchStatus();
    // Poll every 60 s so a Settings toggle reflects here without a hard refresh.
    const id = setInterval(fetchStatus, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user, isMaster]);

  // When the mobile drawer is open we always show the expanded look — never the
  // tight collapsed-icon-rail. On md+ screens we never set `mobileOpen` so this
  // collapses to plain `collapsed`.
  const showCollapsed = collapsed && !mobileOpen;

  // Outer wrapper className shared by both Master + main paths.
  // Mobile: fixed drawer that slides in. Desktop: in-flow flex column.
  const wrapperClass = (gradientBg) => [
    'h-screen text-white transition-transform duration-200 flex flex-col',
    // Mobile drawer behaviour:
    'fixed top-0 left-0 z-50 w-64',
    mobileOpen ? 'translate-x-0' : '-translate-x-full',
    // Desktop behaviour (>=md): in-flow column, no transform, width follows `collapsed`
    'md:static md:translate-x-0 md:flex-shrink-0 md:transition-all md:duration-300',
    showCollapsed ? 'md:w-16' : 'md:w-64',
    gradientBg,
  ].join(' ');

  // Master ONLY sees Master Panel - nothing else
  if (isMaster) {
    const masterOnlyItems = [{ path: '/master', icon: KeyRound, label: 'Master Panel' }];
    const renderLink = (item) => {
      const Icon = item.icon;
      const isActive = location.pathname === item.path;
      return (
        <Link key={item.path} to={item.path} data-testid={`sidebar-link-${item.path.slice(1)}`}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-sm mb-0.5 transition-colors ${isActive ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-700 hover:text-white'}`}>
          <Icon className="h-4.5 w-4.5 flex-shrink-0" style={{ width: 18, height: 18 }} />
          {!showCollapsed && <span className="text-sm font-medium">{item.label}</span>}
        </Link>
      );
    };
    return (
      <>
        {/* Backdrop (mobile only, when drawer is open) */}
        {mobileOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setMobileOpen(false)}
            data-testid="sidebar-backdrop"
          />
        )}
        <div className={wrapperClass('bg-slate-800')}>
          <div className="p-4 flex items-center justify-between border-b border-slate-700">
            {!showCollapsed && (
              <div className="flex items-center gap-2.5 min-w-0">
                <img src="/swami-icon.png" alt="Logo" className="h-8 w-8 rounded-sm object-contain flex-shrink-0" data-testid="sidebar-logo" />
                <span className="text-sm font-bold truncate leading-tight" style={{ fontFamily: 'Chivo' }}>Swami Software OEE</span>
              </div>
            )}
            {showCollapsed && <img src="/swami-icon.png" alt="Logo" className="h-7 w-7 rounded-sm object-contain mx-auto" />}
            {/* Close (mobile) — visible only when drawer is open on small screens */}
            <Button onClick={() => setMobileOpen(false)} variant="ghost" size="sm"
                    className="md:hidden text-white hover:bg-slate-700 h-8 w-8 p-0 flex-shrink-0"
                    data-testid="sidebar-close-btn">
              <X className="h-5 w-5" />
            </Button>
            {/* Collapse toggle (desktop only) */}
            <Button onClick={() => setCollapsed(!collapsed)} variant="ghost" size="sm"
                    className="hidden md:inline-flex text-white hover:bg-slate-700 h-8 w-8 p-0 flex-shrink-0">
              {collapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
            </Button>
          </div>
          <nav className="flex-1 p-2 overflow-y-auto">{masterOnlyItems.map(renderLink)}</nav>
        </div>
      </>
    );
  }

  const restrictedItems = moduleRoutes(user).map(r => ({ ...r, icon: r.key === 'auto_job_card' ? Cpu : Radio }));
  const restricted = restrictedItems.length > 0;

  const mainItems = [
    { path: '/live-dashboard', icon: Activity, label: 'Live Dashboard' },
    ...(autoJobcardEnabled ? [{ path: '/auto-jobcard', icon: Cpu, label: 'Auto Job Card' }] : []),
    ...(rpiEdgeUnlocked ? [{ path: '/rpi-edge', icon: Radio, label: 'Event Logs' }] : []),
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/schedule', icon: Calendar, label: 'Schedule' },
    { path: '/schedule-booking', icon: CalendarCheck, label: 'Machine Loading' },
    { path: '/capacity-sheet', icon: Gauge, label: 'Capacity Sheet' },
    { path: '/production-sheet', icon: ClipboardList, label: 'Job Card' },
    { path: '/oee-analysis', icon: BarChart3, label: 'OEE Analysis' },
    { path: '/final-inspection', icon: ShieldCheck, label: 'Final Inspection' },
    { path: '/graphs', icon: BarChart2, label: 'Reports & Graphs' },
    { path: '/tracker', icon: ClipboardCheck, label: '4W1H' },
    { path: '/hr', icon: UserCog, label: 'HR' },
  ];

  const logItems = [
    { path: '/production-logs', icon: FileText, label: 'Production Logs' },
    { path: '/setup-log', icon: Clock, label: 'Setup Log' },
    { path: '/breakdown-log', icon: AlertTriangle, label: 'Breakdown Log' },
    { path: '/tool-log', icon: Wrench, label: 'Tool Mgmt' },
  ];

  const qualityItems = [
    { path: '/quality/dashboard', icon: Microscope, label: 'Quality Dashboard' },
    { path: '/quality/inspections', icon: ClipboardPen, label: 'QA Inspections' },
    { path: '/quality/approvals', icon: BadgeCheck, label: 'QA HOD Approvals' },
    { path: '/quality/documents', icon: FileCheck2, label: 'Document Master' },
    { path: '/quality/drive', icon: HardDrive, label: 'Drive & QA Settings' },
  ];

  const masterItems = [
    { path: '/item-master', icon: Package, label: 'Item Master' },
    { path: '/contractor-master', icon: Users, label: 'Contractor Master' },
    { path: '/machine-master', icon: Cog, label: 'Machine Master' },
    { path: '/problem-list', icon: AlertTriangle, label: 'Breakdown Master' },
    { path: '/defect-master', icon: ShieldAlert, label: 'Defect Master' },
  ];

  const bottomItems = [
    ...(isAdmin ? [{ path: '/settings', icon: Settings, label: 'Settings' }] : [])
  ];

  const renderLink = (item) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    return (
      <Link
        key={item.path}
        to={item.path}
        data-testid={`sidebar-link-${item.path.slice(1)}`}
        className={`flex items-center gap-3.5 px-3.5 py-3 rounded-md mb-1 transition-colors ${
          isActive
            ? 'text-white'
            : 'text-slate-300 hover:bg-white/10 hover:text-white'
        }`}
        style={isActive ? { background: 'linear-gradient(90deg, #2B5DAE, #3b6fc0)', boxShadow: '0 2px 8px rgba(43,93,174,0.3)' } : {}}
      >
        <Icon className="flex-shrink-0" style={{ width: 20, height: 20 }} />
        {!showCollapsed && <span className="text-[15px] font-semibold">{item.label}</span>}
      </Link>
    );
  };

  return (
    <>
      {/* Mobile-only backdrop behind the drawer */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}
      <div className={wrapperClass('')}
        style={{ background: 'linear-gradient(180deg, #1a2a4a 0%, #162040 100%)' }}>
      <div className="p-4 flex items-center justify-between border-b border-white/10">
        {!showCollapsed && (
          <div className="flex items-center gap-3 min-w-0">
            <img src="/swami-icon.png" alt="Logo" className="h-11 w-11 rounded-md object-contain flex-shrink-0" data-testid="sidebar-logo" />
            <span className="text-[13px] font-black leading-tight whitespace-nowrap" style={{ fontFamily: 'Chivo', color: '#d4a44c' }}>
              Swami Software OEE
            </span>
          </div>
        )}
        {showCollapsed && (
          <img src="/swami-icon.png" alt="Logo" className="h-10 w-10 rounded-md object-contain mx-auto" />
        )}
        {/* Close (mobile only) */}
        <Button onClick={() => setMobileOpen(false)} variant="ghost" size="sm"
                className="md:hidden text-white hover:bg-white/10 h-8 w-8 p-0 flex-shrink-0"
                data-testid="sidebar-close-btn">
          <X className="h-5 w-5" />
        </Button>
        {/* Collapse toggle (desktop only) */}
        <Button
          onClick={() => setCollapsed(!collapsed)}
          variant="ghost"
          size="sm"
          className="hidden md:inline-flex text-white hover:bg-white/10 h-8 w-8 p-0 flex-shrink-0"
        >
          {collapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
        </Button>
      </div>

      <nav className="flex-1 p-2.5 overflow-y-auto">
        {restricted ? restrictedItems.map(renderLink) : <>
        {mainItems.map(renderLink)}

        {/* Logs Group */}
        {!showCollapsed ? (
          <div className="mt-1">
            <button
              onClick={() => setLogsOpen(!logsOpen)}
              className="flex items-center justify-between w-full px-3.5 py-3 text-slate-400 hover:text-white hover:bg-white/10 rounded-md transition-colors"
              data-testid="sidebar-logs-toggle"
            >
              <div className="flex items-center gap-3.5">
                <FileText style={{ width: 20, height: 20 }} />
                <span className="text-[15px] font-semibold">Logs</span>
              </div>
              {logsOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            {logsOpen && (
              <div className="pl-3">
                {logItems.map(renderLink)}
              </div>
            )}
          </div>
        ) : (
          logItems.map(renderLink)
        )}

        {/* Quality Group */}
        {!showCollapsed ? (
          <div className="mt-1">
            <button
              onClick={() => setQualityOpen(!qualityOpen)}
              className="flex items-center justify-between w-full px-3.5 py-3 text-slate-400 hover:text-white hover:bg-white/10 rounded-md transition-colors"
              data-testid="sidebar-quality-toggle"
            >
              <div className="flex items-center gap-3.5">
                <ShieldCheck style={{ width: 20, height: 20 }} />
                <span className="text-[15px] font-semibold">Quality Management</span>
              </div>
              {qualityOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            {qualityOpen && <div className="pl-3">{qualityItems.map(renderLink)}</div>}
          </div>
        ) : (
          qualityItems.map(renderLink)
        )}

        {/* Masters Group */}
        {!showCollapsed ? (
          <div className="mt-1">
            <button
              onClick={() => setMastersOpen(!mastersOpen)}
              className="flex items-center justify-between w-full px-3.5 py-3 text-slate-400 hover:text-white hover:bg-white/10 rounded-md transition-colors"
              data-testid="sidebar-masters-toggle"
            >
              <div className="flex items-center gap-3.5">
                <Layers style={{ width: 20, height: 20 }} />
                <span className="text-[15px] font-semibold">Masters</span>
              </div>
              {mastersOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            {mastersOpen && (
              <div className="pl-3">
                {masterItems.map(renderLink)}
              </div>
            )}
          </div>
        ) : (
          masterItems.map(renderLink)
        )}

        {bottomItems.map(renderLink)}
        </>}
      </nav>
      </div>
    </>
  );
}
